import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { InlineAlert } from '../ui/InlineAlert';
import { RoundedCheckbox } from '../ui/RoundedCheckbox';
import { usePermissions } from '../../contexts/PermissionsContext';
import { canWriteProductCatalog } from './productCatalogAccess';
import {
  SalesProductPersonaLink,
  SalesProductPersonaLinkItemPayload,
  getProductBuyerPersonas,
  listBuyerPersonas,
  replaceProductBuyerPersonas,
} from '../../services/buyerPersonasApi';
import { CatalogMultiSelectModal, CatalogSelectItem } from './CatalogMultiSelectModal';

type ProductBuyerPersonasPanelProps = {
  productId: string;
  readOnly?: boolean;
};

type DraftLink = SalesProductPersonaLinkItemPayload & {
  name: string;
  jobTitle: string;
};

export function ProductBuyerPersonasPanel({
  productId,
  readOnly = false,
}: ProductBuyerPersonasPanelProps) {
  const { canWriteModule } = usePermissions();
  const canWrite = !readOnly && canWriteProductCatalog(canWriteModule);
  const [links, setLinks] = useState<DraftLink[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const linkedIds = useMemo(() => links.map((link) => link.buyerPersonaId), [links]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const productLinks = await getProductBuyerPersonas(productId);
        setLinks(mapLinksToDraft(productLinks));
        setCheckedIds(new Set());
      } catch {
        toast.error('Falha ao carregar personas do produto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  function mapLinksToDraft(productLinks: SalesProductPersonaLink[]): DraftLink[] {
    return productLinks.map((link) => ({
      buyerPersonaId: link.buyerPersonaId,
      name: link.name,
      jobTitle: link.jobTitle,
      isPrimary: link.isPrimary,
      fitScore: link.fitScore,
      sortOrder: link.sortOrder,
    }));
  }

  function updateLink(
    buyerPersonaId: string,
    patch: Partial<Pick<DraftLink, 'isPrimary' | 'fitScore'>>,
  ): void {
    if (!canWrite) return;
    setLinks((current) => {
      if (patch.isPrimary) {
        return current.map((link) => {
          if (link.buyerPersonaId === buyerPersonaId) {
            return { ...link, ...patch };
          }
          return { ...link, isPrimary: false };
        });
      }

      return current.map((link) =>
        link.buyerPersonaId === buyerPersonaId ? { ...link, ...patch } : link,
      );
    });
  }

  function toggleChecked(id: string): void {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function removeOne(id: string): void {
    if (!canWrite) return;
    setLinks((current) => current.filter((link) => link.buyerPersonaId !== id));
    setCheckedIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }

  function removeSelected(): void {
    if (!canWrite || checkedIds.size === 0) return;
    setLinks((current) => current.filter((link) => !checkedIds.has(link.buyerPersonaId)));
    setCheckedIds(new Set());
  }

  function handleModalConfirm(selected: CatalogSelectItem[]): void {
    if (!canWrite || selected.length === 0) return;
    setLinks((current) => {
      const existing = new Set(current.map((link) => link.buyerPersonaId));
      const additions = selected
        .filter((item) => !existing.has(item.id))
        .map((item, index) => ({
          buyerPersonaId: item.id,
          name: item.title,
          jobTitle: item.extra ?? '',
          isPrimary: current.length === 0 && index === 0,
          fitScore: 80,
          sortOrder: current.length + index,
        }));
      return [...current, ...additions];
    });
  }

  async function handleSave(): Promise<void> {
    if (!canWrite) return;
    setSaving(true);
    try {
      const items: SalesProductPersonaLinkItemPayload[] = links.map((link, index) => ({
        buyerPersonaId: link.buyerPersonaId,
        isPrimary: link.isPrimary ?? false,
        fitScore: link.fitScore ?? 0,
        sortOrder: index,
      }));
      await replaceProductBuyerPersonas(productId, items);
      toast.success('Personas do produto atualizadas.');
    } catch {
      toast.error('Falha ao salvar personas do produto.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando personas...</p>;
  }

  return (
    <PageCard title="Personas / ICP" subtitle="Perfis compradores vinculados ao produto.">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-secondary-600">
          Catálogo em{' '}
          <Link to="/sales/buyer-personas" className="erp-link">
            Personas compradoras
          </Link>
          .
        </p>
        {canWrite ? (
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-danger erp-btn-sm"
              disabled={checkedIds.size === 0}
              onClick={removeSelected}
            >
              Excluir selecionados
            </button>
            <button type="button" className="erp-btn-secondary" onClick={() => setModalOpen(true)}>
              Adicionar personas
            </button>
          </div>
        ) : null}
      </div>

      {links.length === 0 ? (
        <InlineAlert>Nenhuma persona vinculada. Use “Adicionar personas”.</InlineAlert>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.buyerPersonaId} className="rounded-lg border border-secondary-200 px-3 py-2">
              <div className="flex items-start gap-3">
                {canWrite ? (
                  <RoundedCheckbox
                    className="mt-1"
                    checked={checkedIds.has(link.buyerPersonaId)}
                    onChange={() => toggleChecked(link.buyerPersonaId)}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-medium text-secondary-900">{link.name}</span>
                      {link.jobTitle ? (
                        <span className="ml-2 text-sm text-secondary-600">— {link.jobTitle}</span>
                      ) : null}
                    </div>
                    {canWrite ? (
                      <DeleteIconButton
                        label="Excluir"
                        onClick={() => removeOne(link.buyerPersonaId)}
                      />
                    ) : null}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    <label className="erp-label-inline text-sm">
                      <input
                        type="checkbox"
                        checked={link.isPrimary ?? false}
                        disabled={!canWrite}
                        onChange={(event) =>
                          updateLink(link.buyerPersonaId, { isPrimary: event.target.checked })
                        }
                      />
                      Persona primária
                    </label>
                    <label className="erp-label-inline text-sm">
                      Fit score
                      <input
                        type="number"
                        min={0}
                        max={100}
                        className="erp-input ml-2 w-20"
                        disabled={!canWrite}
                        value={link.fitScore ?? 0}
                        onChange={(event) =>
                          updateLink(link.buyerPersonaId, { fitScore: Number(event.target.value) })
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="mt-4">
          <button
            type="button"
            className="erp-btn-primary"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvando...' : 'Salvar personas'}
          </button>
        </div>
      ) : null}

      <CatalogMultiSelectModal
        open={modalOpen}
        title="Selecionar personas"
        subtitle="Role para carregar mais itens do catálogo."
        alreadySelectedIds={linkedIds}
        fetchPage={async (page, pageSize, search) => {
          const result = await listBuyerPersonas({ activeOnly: true, page, pageSize, search });
          return {
            items: result.items.map((item) => ({
              id: item.id,
              title: item.name,
              subtitle: item.jobTitle,
              description: item.description,
              extra: item.jobTitle,
            })),
            totalCount: result.totalCount,
            page: result.page,
            pageSize: result.pageSize,
          };
        }}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </PageCard>
  );
}
